/**
 * EmailChangeService
 *
 * Email change and verification flows.
 * Owns: Email-related Edge Functions (request-email-change, resend-email-change, cancel-email-change, get-pending-email-change, verify-email-change)
 *       supabase.auth.refreshSession (to get updated email after verification)
 *
 * Handles: requestEmailChange, verifyEmailChange, getPendingEmailChange, cancelEmailChange, resendEmailVerification
 * Does NOT handle: Direct token table access (moved to Edge Functions with service_role)
 *
 * WU1.5 Task 12.2: Refactored to use Edge Functions instead of direct repository access.
 * All token operations now go through service_role Edge Functions to honor WU4 RLS security model.
 */

import { supabase } from '@/lib/supabase';
import { AuthErrorCode } from '@/types/auth';
import type {
  EmailChangeRequest,
  EmailVerificationRequest,
  AuthOperationResponse
} from '@/types/auth';

/**
 * EmailChangeService
 * Email change and verification operations (delegates to Edge Functions)
 */
class EmailChangeService {
  /**
   * Request email address change for authenticated user
   *
   * Security Model (Established Pattern):
   * - Password verification happens CLIENT-SIDE via supabase.auth.signInWithPassword()
   * - This method refuses to call Edge Function unless password verification succeeds
   * - Edge Function trusts JWT and uses service_role for privileged operations
   *
   * Flow:
   * 1. Verify password client-side (proves user knowledge)
   * 2. Call Edge Function with JWT (server validates, generates token, sends email)
   *
   * Follows Established Pattern:
   * - PasswordService.changePassword() (lines 184-197)
   * - reauth-touch Edge Function
   *
   * @param request - Email change request (currentPassword, newEmail)
   * @param _userId - Current user ID (unused - derived from JWT)
   * @param currentEmail - Current user email (for password verification)
   * @param _userDisplayName - Current user display name (unused)
   * @returns Operation result
   */
  async requestEmailChange(
    request: EmailChangeRequest,
    _userId: string,
    currentEmail: string,
    _userDisplayName: string
  ): Promise<AuthOperationResponse> {
    try {
      // STEP 1: Verify password CLIENT-SIDE (established pattern)
      // This proves the user knows the password before we call the Edge Function
      console.log('🔐 EmailChangeService: Verifying password client-side');

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: currentEmail,
        password: request.currentPassword
      });

      if (signInError) {
        console.error('❌ EmailChangeService: Password verification failed:', signInError);
        return {
          success: false,
          error: 'INVALID_CREDENTIALS' as AuthErrorCode,
          message: 'Current password is incorrect'
        };
      }

      console.log('✅ EmailChangeService: Password verified, calling Edge Function');

      // STEP 2: Call Edge Function (password already verified, JWT auto-added)
      // Edge Function will trust the JWT and use service_role for DB operations
      const { data, error } = await supabase.functions.invoke('request-email-change', {
        body: {
          newEmail: request.newEmail
          // NOTE: NO currentPassword, NO userId, NO currentEmail
          // Identity derived from JWT by Edge Function
        }
      });

      // Note: When Edge Function returns 4xx/5xx, supabase.functions.invoke() sets error
      // and data is null. The error.context is a Response object that we need to parse
      if (error) {
        console.error('❌ EmailChangeService: request-email-change returned non-2xx:', error);

        const errorMessages: Record<string, string> = {
          'INVALID_PASSWORD': 'Current password is incorrect',
          'EMAIL_ALREADY_IN_USE': 'This email address is already in use',
          'INVALID_EMAIL_FORMAT': 'Please enter a valid email address',
          'SAME_EMAIL': 'New email must be different from current email',
          'UNAUTHORIZED': 'You must be logged in to change your email',
          'TEMPLATE_NOT_FOUND': 'Email verification template not configured',
          'EMAIL_SEND_FAILED': 'Failed to send verification email. Please try again.'
        };

        // FunctionsHttpError has a Response object in error.context
        // We need to read the JSON from it
        try {
          // Check if error has context property (FunctionsError interface)
          const errorContext = 'context' in error ? error.context : null;
          if (errorContext && typeof errorContext.json === 'function') {
            const errorBody = await errorContext.json();
            console.log('📦 EmailChangeService: Parsed error body:', errorBody);

            if (errorBody?.error) {
              const errorCode = errorBody.error;
              return {
                success: false,
                error: errorCode as AuthErrorCode,
                message: errorMessages[errorCode] || 'Failed to request email change'
              };
            }
          }
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
        }

        // Generic error if we can't parse details
        return {
          success: false,
          message: 'Failed to request email change. Please try again.'
        };
      }

      // Map successful Edge Function response to AuthOperationResponse
      if (data?.success) {
        return {
          success: true,
          message: `Verification email sent to ${request.newEmail}`
        };
      }

      // Shouldn't reach here, but handle just in case
      return {
        success: false,
        message: 'Failed to request email change'
      };
    } catch (error) {
      console.error('❌ EmailChangeService: Email change request error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred while requesting email change'
      };
    }
  }

  /**
   * Verify email change with token
   *
   * Already uses Edge Function (verify-email-change) - no changes needed.
   * Refreshes session after successful verification.
   *
   * @param request - Email verification request (verificationToken)
   * @returns Operation result with newEmail
   */
  async verifyEmailChange(request: EmailVerificationRequest): Promise<AuthOperationResponse & { newEmail?: string }> {
    console.log('✅ EmailChangeService: Email verification request');

    try {
      // Call Edge Function to verify token and update email (already correct)
      const { data: verificationResult, error: verificationError } = await supabase.functions.invoke(
        'verify-email-change',
        {
          body: { verificationToken: request.verificationToken }
        }
      );

      if (verificationError) {
        console.error('❌ EmailChangeService: Email verification failed:', verificationError);
        return {
          success: false,
          error: 'EMAIL_VERIFICATION_FAILED' as AuthErrorCode,
          message: 'Failed to verify email change. Please try again.'
        };
      }

      if (!verificationResult.success) {
        console.error('❌ EmailChangeService: Email verification failed:', verificationResult.error);

        // Map error codes
        let errorCode: AuthErrorCode = 'INVALID_VERIFICATION_TOKEN' as AuthErrorCode;
        if (verificationResult.error === 'TOKEN_EXPIRED') {
          errorCode = 'VERIFICATION_TOKEN_EXPIRED' as AuthErrorCode;
        }

        return {
          success: false,
          error: errorCode,
          message: verificationResult.message || 'Email verification failed'
        };
      }

      console.log('✅ EmailChangeService: Email verification successful');

      // CRITICAL: Refresh session to get updated email from Supabase Auth
      // This ensures the UI displays the new email immediately without hard reload
      try {
        const { data: { session } } = await supabase.auth.refreshSession();
        if (session?.user) {
          console.log('✅ EmailChangeService: Session refreshed with new email:', session.user.email);
        }
      } catch (refreshError) {
        console.warn('⚠️ EmailChangeService: Session refresh failed (user may not be logged in):', refreshError);
      }

      return {
        success: true,
        message: 'Email address updated successfully',
        newEmail: verificationResult.newEmail
      };
    } catch (error) {
      console.error('❌ EmailChangeService: Email verification error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred while verifying email change'
      };
    }
  }

  /**
   * Get pending email change for current user
   *
   * Delegates to get-pending-email-change Edge Function.
   * This is the ONLY source of truth for pending state (no direct repository access).
   *
   * @param userId - Current user ID (unused - Edge Function gets from JWT)
   * @returns Pending email change info or null
   */
  async getPendingEmailChange(_userId: string): Promise<{ newEmail: string; createdAt: string } | null> {
    try {
      // Delegate to Edge Function (service_role access for token table)
      const { data, error } = await supabase.functions.invoke('get-pending-email-change');

      if (error) {
        console.error('❌ EmailChangeService: get-pending-email-change Edge Function error:', error);
        return null;
      }

      // Edge Function returns { pending: true/false, newEmail?, createdAt? }
      if (data?.pending && data.newEmail) {
        return {
          newEmail: data.newEmail,
          createdAt: data.createdAt
        };
      }

      return null;
    } catch (error) {
      console.error('❌ EmailChangeService: Failed to get pending email change:', error);
      return null;
    }
  }

  /**
   * Cancel pending email change
   *
   * Delegates to cancel-email-change Edge Function which marks token as used.
   *
   * @param userId - Current user ID (unused - Edge Function gets from JWT)
   * @returns Operation result
   */
  async cancelEmailChange(_userId: string): Promise<AuthOperationResponse> {
    console.log('🚫 EmailChangeService: Cancel email change request');

    try {
      // Delegate to Edge Function (service_role access for token table)
      const { data, error } = await supabase.functions.invoke('cancel-email-change');

      if (error) {
        console.error('❌ EmailChangeService: cancel-email-change Edge Function error:', error);
        return {
          success: false,
          message: 'Failed to cancel email change. Please try again.'
        };
      }

      // Edge Function always returns { cancelled: true } if authenticated (idempotent)
      if (data?.cancelled) {
        console.log('✅ EmailChangeService: Email change cancelled successfully');
        return {
          success: true,
          message: 'Email change request cancelled'
        };
      }

      return {
        success: false,
        message: 'Failed to cancel email change'
      };
    } catch (error) {
      console.error('❌ EmailChangeService: Cancel email change error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred while cancelling email change'
      };
    }
  }

  /**
   * Resend email verification
   *
   * Delegates to resend-email-change Edge Function which:
   * - Reuses existing token if still valid
   * - Generates fresh token for SAME email if expired
   * - No password required (user already proved identity)
   *
   * @param userId - Current user ID (unused - Edge Function gets from JWT)
   * @param userEmail - Current user email (unused - Edge Function gets from JWT)
   * @param userDisplayName - Current user display name (unused - Edge Function gets from JWT)
   * @returns Operation result
   */
  async resendEmailVerification(
    _userId: string,
    _userEmail: string,
    _userDisplayName: string
  ): Promise<AuthOperationResponse> {
    console.log('📤 EmailChangeService: Resend email verification request');

    try {
      // Delegate to Edge Function (service_role access for token table)
      // No password required - Edge Function reuses/renews existing pending token
      const { data, error } = await supabase.functions.invoke('resend-email-change');

      if (error) {
        console.error('❌ EmailChangeService: resend-email-change Edge Function error:', error);
        return {
          success: false,
          message: 'Failed to resend verification email. Please try again.'
        };
      }

      // Map Edge Function response
      if (data?.resent) {
        console.log('✅ EmailChangeService: Email verification resent successfully');
        return {
          success: true,
          message: 'Verification email resent'
        };
      }

      // Handle error codes from Edge Function
      const errorCode = data?.error || 'UNKNOWN_ERROR';
      const errorMessages: Record<string, string> = {
        'NO_PENDING_CHANGE': 'No pending email change found',
        'UNAUTHORIZED': 'You must be logged in to resend verification',
        'TEMPLATE_NOT_FOUND': 'Email verification template not configured',
        'EMAIL_SEND_FAILED': 'Failed to resend verification email. Please try again.'
      };

      return {
        success: false,
        error: errorCode as AuthErrorCode,
        message: errorMessages[errorCode] || 'Failed to resend verification email'
      };
    } catch (error) {
      console.error('❌ EmailChangeService: Resend email verification error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred while resending verification email'
      };
    }
  }
}

// Export singleton instance
export const emailChangeService = new EmailChangeService();
